import { useCallback, useEffect, type RefObject } from 'react'

import { isOfficeViewerFindShortcut } from '@/components/office/OfficeViewer/OfficeViewerFindBar'

const OPEN_OFFICE_VIEWER_OVERLAY_SELECTOR =
  '[data-office-viewer-overlay][data-state="open"]'
const OFFICE_VIEWER_SCOPE_SELECTOR = '[data-office-viewer-scope]'

const hasOpenOfficeViewerOverlay = () =>
  document.querySelector(OPEN_OFFICE_VIEWER_OVERLAY_SELECTOR) != null

interface UseOfficeViewerFindKeyboardOptions {
  isOpen: boolean
  onClose: () => void
  onOpen: () => void
  viewerRootRef: RefObject<HTMLElement | null>
}

/**
 * Keeps Find keyboard behavior consistent when an Office viewer is embedded
 * in a dialog whose toolbar is outside the viewer root. Foreground portalled
 * controls retain first claim on Escape and Command/Ctrl-F.
 */
export const useOfficeViewerFindKeyboard = ({
  isOpen,
  onClose,
  onOpen,
  viewerRootRef,
}: UseOfficeViewerFindKeyboardOptions) => {
  const handleViewerKeyDown = useCallback(
    (event: React.KeyboardEvent<HTMLElement>) => {
      if (hasOpenOfficeViewerOverlay()) {
        if (isOfficeViewerFindShortcut(event)) {
          event.preventDefault()
          event.stopPropagation()
        }
        return false
      }
      if (event.key === 'Escape' && isOpen) {
        event.preventDefault()
        event.stopPropagation()
        onClose()
        return true
      }
      if (!isOfficeViewerFindShortcut(event)) {
        return false
      }

      event.preventDefault()
      event.stopPropagation()
      onOpen()
      return true
    },
    [isOpen, onClose, onOpen],
  )

  useEffect(() => {
    const handleWindowKeyDown = (event: KeyboardEvent) => {
      const viewer = viewerRootRef.current
      const eventTarget = event.target
      if (!viewer) {
        return
      }

      if (hasOpenOfficeViewerOverlay()) {
        if (isOfficeViewerFindShortcut(event)) {
          event.preventDefault()
        }
        return
      }

      const targetIsInsideViewer =
        eventTarget instanceof Node && viewer.contains(eventTarget)
      const viewerScope = viewer.closest(OFFICE_VIEWER_SCOPE_SELECTOR)
      const targetIsInsideViewerScope =
        viewerScope != null &&
        eventTarget instanceof Node &&
        viewerScope.contains(eventTarget)
      const viewerDialog = viewer.closest('[role="dialog"]')
      const openDialogs = document.querySelectorAll('[role="dialog"]')
      const viewerIsInTopmostDialog =
        viewerDialog != null &&
        openDialogs.item(openDialogs.length - 1) === viewerDialog

      if (
        event.key === 'Escape' &&
        isOpen &&
        (targetIsInsideViewer ||
          targetIsInsideViewerScope ||
          viewerIsInTopmostDialog)
      ) {
        event.preventDefault()
        event.stopImmediatePropagation()
        onClose()
        return
      }

      if (
        targetIsInsideViewer ||
        (!targetIsInsideViewerScope && !viewerIsInTopmostDialog)
      ) {
        return
      }
      if (!isOfficeViewerFindShortcut(event)) {
        return
      }

      event.preventDefault()
      event.stopPropagation()
      onOpen()
    }

    window.addEventListener('keydown', handleWindowKeyDown, true)
    return () => {
      window.removeEventListener('keydown', handleWindowKeyDown, true)
    }
  }, [isOpen, onClose, onOpen, viewerRootRef])

  return handleViewerKeyDown
}
